import { forwardRef, type ElementType, type HTMLAttributes } from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

const textVariants = cva("text-foreground", {
    variants: {
        variant: {
            // Display
            "display-large": "text-6xl font-black",
            "display-medium": "text-5xl font-black",
            "display-small": "text-4xl font-black",
            "display-tiny": "text-3xl font-black",
            // Headline
            "headline-large": "text-3xl font-black",
            "headline-medium": "text-2xl font-black",
            "headline-small": "text-xl font-black",
            // Title
            "title-large": "text-lg font-bold",
            "title-medium": "text-base font-bold",
            "title-small": "text-sm font-bold",
            // Body
            "body-large": "text-lg font-normal",
            "body-medium": "text-base font-normal",
            "body-small": "text-sm font-normal",
            "body-tiny": "text-xs font-normal",
            // Label
            "label-large": "text-base font-medium",
            "label-medium": "text-sm font-medium",
            "label-small": "text-xs font-medium",
        },
    },
    defaultVariants: {
        variant: "body-medium",
    },
})

export interface TextProps
    extends HTMLAttributes<HTMLElement>,
    VariantProps<typeof textVariants> {
    as?: ElementType
    tKey?: string
}

const Text = forwardRef<HTMLElement, TextProps>(
    ({ className, variant, as: Component = "p", tKey, children, ...props }, ref) => {
        const { t } = useTranslation()
        return (
            <Component
                className={cn(textVariants({ variant, className }))}
                ref={ref}
                {...props}
            >
                {tKey ? t(tKey) : children}
            </Component>
        )
    }
)
Text.displayName = "Text"

export { Text, textVariants }
